import React, { useState, useEffect } from 'react';
import merchantKycService from '../services/merchantKycService';
import { INDIAN_STATES } from '../utils/indianStates';
import './MerchantKycProfile.css';

const MerchantKycProfile = ({ merchantProfile, onProfileUpdate }) => {
  const [formData, setFormData] = useState({
    personalInfo: { ...merchantProfile.personalInfo },
    businessInfo: { ...merchantProfile.businessInfo },
    bankInfo: { ...merchantProfile.bankInfo }
  });
  const [activeSection, setActiveSection] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [savedSections, setSavedSections] = useState({
    personal: false,
    business: false,
    bank: false
  });
  const [lastSaved, setLastSaved] = useState({
    personal: null,
    business: null,
    bank: null
  });
  const [errors, setErrors] = useState({});
  const [sectionChanges, setSectionChanges] = useState({
    personal: false,
    business: false,
    bank: false
  });

  // Update form data when merchantProfile changes (merchant switching)
  useEffect(() => {
    setFormData({
      personalInfo: { ...merchantProfile.personalInfo },
      businessInfo: { ...merchantProfile.businessInfo },
      bankInfo: { ...merchantProfile.bankInfo }
    });
    
    // Reset all states when merchant changes
    setSavedSections({
      personal: false,
      business: false,
      bank: false
    });
    
    setSectionChanges({
      personal: false,
      business: false,
      bank: false
    });
    
    setLastSaved({
      personal: null,
      business: null,
      bank: null
    });
    
    setErrors({});
    
    console.log('🔄 MerchantKycProfile: Updated form data for merchant:', merchantProfile.merchantId);
  }, [merchantProfile.merchantId]); // Trigger when merchant ID changes

  // Initialize saved state based on existing data
  useEffect(() => {
    // Check if sections have data and mark them as potentially saved
    const personalComplete = merchantProfile.personalInfo.fullName && merchantProfile.personalInfo.email;
    const businessComplete = merchantProfile.businessInfo.businessName && merchantProfile.businessInfo.pan;
    const bankComplete = merchantProfile.bankInfo.accountNumber && merchantProfile.bankInfo.ifscCode;

    // Only set as saved if we haven't made any changes yet (initial load only)
    setSavedSections({
      personal: personalComplete,
      business: businessComplete,
      bank: bankComplete
    });

    // Reset section changes on initial load
    setSectionChanges({
      personal: false,
      business: false,
      bank: false
    });
  }, [merchantProfile.merchantId]); // Only run when merchant ID changes

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));

    // Mark the specific section as having changes
    const sectionKey = section.replace('Info', '');
    
    // Update both states together to ensure consistency
    setSectionChanges(prev => ({
      ...prev,
      [sectionKey]: true
    }));
    
    setSavedSections(prev => ({
      ...prev,
      [sectionKey]: false
    }));

    // Clear error for this field
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: null
      }));
    }
  };

  const validateCurrentSection = () => {
    const newErrors = {};
    
    if (activeSection === 'personal') {
      // Personal Info Validation
      if (!formData.personalInfo.fullName?.trim()) {
        newErrors['personalInfo.fullName'] = 'Full name is required';
      }
      if (!formData.personalInfo.email?.trim()) {
        newErrors['personalInfo.email'] = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.personalInfo.email)) {
        newErrors['personalInfo.email'] = 'Invalid email format';
      }
      if (!formData.personalInfo.phone?.trim()) {
        newErrors['personalInfo.phone'] = 'Phone number is required';
      } else if (!/^\d{10}$/.test(formData.personalInfo.phone.replace(/\D/g, ''))) {
        newErrors['personalInfo.phone'] = 'Invalid phone number (10 digits required)';
      }
    } else if (activeSection === 'business') {
      // Business Info Validation
      if (!formData.businessInfo.businessName?.trim()) {
        newErrors['businessInfo.businessName'] = 'Business name is required';
      }
      if (!formData.businessInfo.businessType?.trim()) {
        newErrors['businessInfo.businessType'] = 'Business type is required';
      }
      if (!formData.businessInfo.pan?.trim()) {
        newErrors['businessInfo.pan'] = 'PAN number is required';
      } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.businessInfo.pan)) {
        newErrors['businessInfo.pan'] = 'Invalid PAN format (e.g., ABCDE1234F)';
      }
      if (!formData.businessInfo.registeredAddress?.trim()) {
        newErrors['businessInfo.registeredAddress'] = 'Registered address is required';
      }
      if (!formData.businessInfo.city?.trim()) {
        newErrors['businessInfo.city'] = 'City is required';
      }
      if (!formData.businessInfo.state?.trim()) {
        newErrors['businessInfo.state'] = 'State is required';
      }
      if (!formData.businessInfo.pincode?.trim()) {
        newErrors['businessInfo.pincode'] = 'Pincode is required';
      } else if (!/^\d{6}$/.test(formData.businessInfo.pincode)) {
        newErrors['businessInfo.pincode'] = 'Invalid pincode (6 digits required)';
      }
    } else if (activeSection === 'bank') {
      // Bank Info Validation
      if (!formData.bankInfo.accountHolderName?.trim()) {
        newErrors['bankInfo.accountHolderName'] = 'Account holder name is required';
      }
      if (!formData.bankInfo.accountNumber?.trim()) {
        newErrors['bankInfo.accountNumber'] = 'Account number is required';
      } else if (!/^\d{9,18}$/.test(formData.bankInfo.accountNumber)) {
        newErrors['bankInfo.accountNumber'] = 'Invalid account number (9-18 digits)';
      }
      if (!formData.bankInfo.ifscCode?.trim()) {
        newErrors['bankInfo.ifscCode'] = 'IFSC code is required';
      } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.bankInfo.ifscCode)) {
        newErrors['bankInfo.ifscCode'] = 'Invalid IFSC code format';
      }
      if (!formData.bankInfo.bankName?.trim()) {
        newErrors['bankInfo.bankName'] = 'Bank name is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateCurrentSection()) {
      if (window.showNotification) {
        window.showNotification('Please fix the validation errors', 'error');
      }
      return;
    }

    setSaving(true);
    
    try {
      // Get current profile from service
      const currentProfile = merchantKycService.getMerchantProfile(merchantProfile.merchantId);
      
      // Create update object with only the current section
      const updateData = {
        personalInfo: currentProfile.personalInfo,
        businessInfo: currentProfile.businessInfo,
        bankInfo: currentProfile.bankInfo
      };
      
      // Update only the current section
      if (activeSection === 'personal') {
        updateData.personalInfo = formData.personalInfo;
      } else if (activeSection === 'business') {
        updateData.businessInfo = formData.businessInfo;
      } else if (activeSection === 'bank') {
        updateData.bankInfo = formData.bankInfo;
      }

      const updatedProfile = merchantKycService.updateMerchantProfile(
        merchantProfile.merchantId,
        updateData
      );

      if (updatedProfile) {
        // Update parent component with new profile
        onProfileUpdate(updatedProfile);
        
        // Update last saved time for current section FIRST
        const saveTime = new Date();
        setLastSaved(prev => ({
          ...prev,
          [activeSection]: saveTime
        }));
        
        // Mark current section as saved and clear changes - CRITICAL ORDER
        setSavedSections(prev => ({
          ...prev,
          [activeSection]: true
        }));
        
        setSectionChanges(prev => ({
          ...prev,
          [activeSection]: false
        }));
        
        // Show success notification
        if (window.showNotification) {
          const sectionName = activeSection === 'personal' ? 'Personal Information' :
                             activeSection === 'business' ? 'Business Information' : 'Bank Information';
          window.showNotification(`${sectionName} saved successfully!`, 'success');
        }

        // Recalculate completion percentage and update KYC status
        const newCompletionPercentage = merchantKycService.calculateCompletionPercentage(merchantProfile.merchantId);
        if (newCompletionPercentage !== updatedProfile.kycStatus.completionPercentage) {
          merchantKycService.updateMerchantKycStatus(merchantProfile.merchantId, {
            completionPercentage: newCompletionPercentage,
            status: newCompletionPercentage >= 100 ? 'PENDING_REVIEW' : 'IN_PROGRESS'
          });
        }

        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('kycProfileUpdated', {
          detail: { merchantId: merchantProfile.merchantId, section: activeSection, completionPercentage: newCompletionPercentage }
        }));

      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setSavedSections(prev => ({
        ...prev,
        [activeSection]: false
      }));
      if (window.showNotification) {
        window.showNotification('Failed to save profile. Please try again.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const getSectionIcon = (section) => {
    const icons = {
      personal: '👤',
      business: '🏢',
      bank: '🏦'
    };
    return icons[section] || '📋';
  };

  const getSectionProgress = (section) => {
    const data = formData[section];
    const fields = Object.keys(data);
    const filledFields = fields.filter(field => data[field]?.toString().trim());
    return Math.round((filledFields.length / fields.length) * 100);
  };

  const isSectionComplete = (section) => {
    const progress = getSectionProgress(section);
    return progress >= 80; // Consider 80% or more as complete
  };

  const isSectionSaved = (section) => {
    const sectionKey = section.replace('Info', '');
    return savedSections[sectionKey];
  };

  const hasSectionChanges = (section) => {
    const sectionKey = section.replace('Info', '');
    return sectionChanges[sectionKey];
  };

  const getSectionLastSaved = (section) => {
    const sectionKey = section.replace('Info', '');
    return lastSaved[sectionKey];
  };

  const renderPersonalInfo = () => (
    <div className="form-section">
      <h3>👤 Personal Information</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Full Name *</label>
          <input
            type="text"
            value={formData.personalInfo.fullName || ''}
            onChange={(e) => handleInputChange('personalInfo', 'fullName', e.target.value)}
            className={errors['personalInfo.fullName'] ? 'error' : ''}
            placeholder="Enter your full name"
          />
          {errors['personalInfo.fullName'] && (
            <span className="error-message">{errors['personalInfo.fullName']}</span>
          )}
        </div>

        <div className="form-group">
          <label>Email Address *</label>
          <input
            type="email"
            value={formData.personalInfo.email || ''}
            onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
            className={errors['personalInfo.email'] ? 'error' : ''}
            placeholder="Enter your email address"
          />
          {errors['personalInfo.email'] && (
            <span className="error-message">{errors['personalInfo.email']}</span>
          )}
        </div>

        <div className="form-group">
          <label>Phone Number *</label>
          <input
            type="tel"
            value={formData.personalInfo.phone || ''}
            onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
            className={errors['personalInfo.phone'] ? 'error' : ''}
            placeholder="Enter your phone number"
          />
          {errors['personalInfo.phone'] && (
            <span className="error-message">{errors['personalInfo.phone']}</span>
          )}
        </div>

        <div className="form-group">
          <label>Date of Birth</label>
          <input
            type="date"
            value={formData.personalInfo.dateOfBirth || ''}
            onChange={(e) => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Nationality</label>
          <select
            value={formData.personalInfo.nationality || 'Indian'}
            onChange={(e) => handleInputChange('personalInfo', 'nationality', e.target.value)}
          >
            <option value="Indian">Indian</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label>Occupation</label>
          <input
            type="text"
            value={formData.personalInfo.occupation || ''}
            onChange={(e) => handleInputChange('personalInfo', 'occupation', e.target.value)}
            placeholder="Enter your occupation"
          />
        </div>
      </div>
    </div>
  );

  const renderBusinessInfo = () => (
    <div className="form-section">
      <h3>🏢 Business Information</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Business Name *</label>
          <input
            type="text"
            value={formData.businessInfo.businessName || ''}
            onChange={(e) => handleInputChange('businessInfo', 'businessName', e.target.value)}
            className={errors['businessInfo.businessName'] ? 'error' : ''}
            placeholder="Enter your business name"
          />
          {errors['businessInfo.businessName'] && (
            <span className="error-message">{errors['businessInfo.businessName']}</span>
          )}
        </div>

        <div className="form-group">
          <label>Business Type *</label>
          <select
            value={formData.businessInfo.businessType || ''}
            onChange={(e) => handleInputChange('businessInfo', 'businessType', e.target.value)}
            className={errors['businessInfo.businessType'] ? 'error' : ''}
          >
            <option value="">Select business type</option>
            <option value="Sole Proprietorship">Sole Proprietorship</option>
            <option value="Partnership">Partnership</option>
            <option value="Private Limited">Private Limited</option>
            <option value="Public Limited">Public Limited</option>
            <option value="LLP">Limited Liability Partnership (LLP)</option>
            <option value="Other">Other</option>
          </select>
          {errors['businessInfo.businessType'] && (
            <span className="error-message">{errors['businessInfo.businessType']}</span>
          )}
        </div>

        <div className="form-group">
          <label>PAN Number *</label>
          <input
            type="text"
            value={formData.businessInfo.pan || ''}
            onChange={(e) => handleInputChange('businessInfo', 'pan', e.target.value.toUpperCase())}
            className={errors['businessInfo.pan'] ? 'error' : ''}
            placeholder="ABCDE1234F"
            maxLength="10"
          />
          {errors['businessInfo.pan'] && (
            <span className="error-message">{errors['businessInfo.pan']}</span>
          )}
        </div>

        <div className="form-group">
          <label>GSTIN (Optional)</label>
          <input
            type="text"
            value={formData.businessInfo.gstin || ''}
            onChange={(e) => handleInputChange('businessInfo', 'gstin', e.target.value.toUpperCase())}
            placeholder="Enter GSTIN if applicable"
            maxLength="15"
          />
        </div>

        <div className="form-group full-width">
          <label>Registered Address *</label>
          <textarea
            value={formData.businessInfo.registeredAddress || ''}
            onChange={(e) => handleInputChange('businessInfo', 'registeredAddress', e.target.value)}
            className={errors['businessInfo.registeredAddress'] ? 'error' : ''}
            placeholder="Enter complete registered address"
            rows="3"
          />
          {errors['businessInfo.registeredAddress'] && (
            <span className="error-message">{errors['businessInfo.registeredAddress']}</span>
          )}
        </div>

        <div className="form-group">
          <label>City *</label>
          <input
            type="text"
            value={formData.businessInfo.city || ''}
            onChange={(e) => handleInputChange('businessInfo', 'city', e.target.value)}
            className={errors['businessInfo.city'] ? 'error' : ''}
            placeholder="Enter city"
          />
          {errors['businessInfo.city'] && (
            <span className="error-message">{errors['businessInfo.city']}</span>
          )}
        </div>

        <div className="form-group">
          <label>State *</label>
          <select
            value={formData.businessInfo.state || ''}
            onChange={(e) => handleInputChange('businessInfo', 'state', e.target.value)}
            className={errors['businessInfo.state'] ? 'error' : ''}
          >
            <option value="">Select state</option>
            {INDIAN_STATES.map(state => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </select>
          {errors['businessInfo.state'] && (
            <span className="error-message">{errors['businessInfo.state']}</span>
          )}
        </div>

        <div className="form-group">
          <label>Pincode *</label>
          <input
            type="text"
            value={formData.businessInfo.pincode || ''}
            onChange={(e) => handleInputChange('businessInfo', 'pincode', e.target.value)}
            className={errors['businessInfo.pincode'] ? 'error' : ''}
            placeholder="Enter pincode"
            maxLength="6"
          />
          {errors['businessInfo.pincode'] && (
            <span className="error-message">{errors['businessInfo.pincode']}</span>
          )}
        </div>

        <div className="form-group">
          <label>Business Category</label>
          <select
            value={formData.businessInfo.businessCategory || ''}
            onChange={(e) => handleInputChange('businessInfo', 'businessCategory', e.target.value)}
          >
            <option value="">Select category</option>
            <option value="E-commerce">E-commerce</option>
            <option value="Retail">Retail</option>
            <option value="Services">Services</option>
            <option value="Manufacturing">Manufacturing</option>
            <option value="Technology">Technology</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Education">Education</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label>Annual Turnover</label>
          <select
            value={formData.businessInfo.annualTurnover || ''}
            onChange={(e) => handleInputChange('businessInfo', 'annualTurnover', e.target.value)}
          >
            <option value="">Select turnover range</option>
            <option value="Below 1 Lakh">Below ₹1 Lakh</option>
            <option value="1-10 Lakh">₹1 - ₹10 Lakh</option>
            <option value="10-50 Lakh">₹10 - ₹50 Lakh</option>
            <option value="50 Lakh - 1 Crore">₹50 Lakh - ₹1 Crore</option>
            <option value="Above 1 Crore">Above ₹1 Crore</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderBankInfo = () => (
    <div className="form-section">
      <h3>🏦 Bank Information</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Account Holder Name *</label>
          <input
            type="text"
            value={formData.bankInfo.accountHolderName || ''}
            onChange={(e) => handleInputChange('bankInfo', 'accountHolderName', e.target.value)}
            className={errors['bankInfo.accountHolderName'] ? 'error' : ''}
            placeholder="Enter account holder name"
          />
          {errors['bankInfo.accountHolderName'] && (
            <span className="error-message">{errors['bankInfo.accountHolderName']}</span>
          )}
        </div>

        <div className="form-group">
          <label>Account Number *</label>
          <input
            type="text"
            value={formData.bankInfo.accountNumber || ''}
            onChange={(e) => handleInputChange('bankInfo', 'accountNumber', e.target.value)}
            className={errors['bankInfo.accountNumber'] ? 'error' : ''}
            placeholder="Enter account number"
          />
          {errors['bankInfo.accountNumber'] && (
            <span className="error-message">{errors['bankInfo.accountNumber']}</span>
          )}
        </div>

        <div className="form-group">
          <label>IFSC Code *</label>
          <input
            type="text"
            value={formData.bankInfo.ifscCode || ''}
            onChange={(e) => handleInputChange('bankInfo', 'ifscCode', e.target.value.toUpperCase())}
            className={errors['bankInfo.ifscCode'] ? 'error' : ''}
            placeholder="ABCD0123456"
            maxLength="11"
          />
          {errors['bankInfo.ifscCode'] && (
            <span className="error-message">{errors['bankInfo.ifscCode']}</span>
          )}
        </div>

        <div className="form-group">
          <label>Bank Name *</label>
          <input
            type="text"
            value={formData.bankInfo.bankName || ''}
            onChange={(e) => handleInputChange('bankInfo', 'bankName', e.target.value)}
            className={errors['bankInfo.bankName'] ? 'error' : ''}
            placeholder="Enter bank name"
          />
          {errors['bankInfo.bankName'] && (
            <span className="error-message">{errors['bankInfo.bankName']}</span>
          )}
        </div>

        <div className="form-group">
          <label>Branch Name</label>
          <input
            type="text"
            value={formData.bankInfo.branchName || ''}
            onChange={(e) => handleInputChange('bankInfo', 'branchName', e.target.value)}
            placeholder="Enter branch name"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="merchant-kyc-profile">
      {/* Section Navigation */}
      <div className="section-nav">
        {[
          { id: 'personal', label: 'Personal Info' },
          { id: 'business', label: 'Business Info' },
          { id: 'bank', label: 'Bank Info' }
        ].map(section => (
          <button
            key={section.id}
            className={`section-nav-btn ${activeSection === section.id ? 'active' : ''} ${
              isSectionComplete(section.id + 'Info') ? 'completed' : ''
            } ${
              savedSections[section.id] ? 'saved' : ''
            }`}
            onClick={() => setActiveSection(section.id)}
          >
            <span className="section-icon">{getSectionIcon(section.id)}</span>
            <span className="section-label">{section.label}</span>
            <div className="section-progress">
              <div 
                className="progress-fill" 
                style={{ width: `${getSectionProgress(section.id + 'Info')}%` }}
              ></div>
            </div>
            {savedSections[section.id] && (
              <span className="section-saved-indicator">✓</span>
            )}
          </button>
        ))}
      </div>

      {/* Form Content */}
      <div className="profile-form">
        {activeSection === 'personal' && renderPersonalInfo()}
        {activeSection === 'business' && renderBusinessInfo()}
        {activeSection === 'bank' && renderBankInfo()}

        {/* Save Button */}
        <div className="form-actions">
          <button 
            className={`btn btn-lg ${
              savedSections[activeSection] && !sectionChanges[activeSection] ? 'btn-success' : 'btn-primary'
            }`}
            onClick={handleSave}
            disabled={saving || (savedSections[activeSection] && !sectionChanges[activeSection])}
          >
            {(() => {
              const isSaved = savedSections[activeSection];
              const hasChanges = sectionChanges[activeSection];
              const sectionName = activeSection === 'personal' ? 'Personal Info' : 
                                 activeSection === 'business' ? 'Business Info' : 'Bank Info';

              if (saving) {
                return (
                  <>
                    <span className="spinner"></span>
                    Saving {sectionName}...
                  </>
                );
              } else if (isSaved && !hasChanges) {
                return (
                  <>
                    ✅ {sectionName} Saved
                  </>
                );
              } else {
                return (
                  <>
                    💾 Save {sectionName}
                  </>
                );
              }
            })()}
          </button>
          
          {/* Save Status Message */}
          {savedSections[activeSection] && !sectionChanges[activeSection] && lastSaved[activeSection] && (
            <div className="save-status">
              <span className="save-success">
                ✅ {activeSection === 'personal' ? 'Personal Information' : 
                     activeSection === 'business' ? 'Business Information' : 'Bank Information'} saved successfully at {lastSaved[activeSection].toLocaleTimeString()}
              </span>
            </div>
          )}
          
          {sectionChanges[activeSection] && (
            <div className="save-status">
              <span className="unsaved-changes">
                ⚠️ You have unsaved changes in {activeSection === 'personal' ? 'Personal Information' : 
                                                activeSection === 'business' ? 'Business Information' : 'Bank Information'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MerchantKycProfile;